import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDatabase,
  faCloudArrowDown,
  faFileLines,
  faFlask,
  faCircleCheck,
  faLayerGroup,
  faListCheck,
  faFaceSmile,
} from "@fortawesome/free-solid-svg-icons";
import getDataCounts from "../../../../API/dataCounts";

const formatNumber = (num) =>
  typeof num === "number" ? num.toLocaleString("en-IN") : "—";

/* Count-up animation hook */
const useCountUp = (target, active, duration = 900) => {
  const [val, setVal] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (!active || typeof target !== "number") return;
    const start = performance.now();
    const animate = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(Math.round(target * eased));
      if (p < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [target, active, duration]);

  return typeof target === "number" ? val : null;
};

const SECTIONS = [
  {
    title: "Raw Data",
    group: "rawData",
    accent: "#6366F1",
    headerGradient: "linear-gradient(135deg, #6A11CB 0%, #2575FC 100%)",
    cards: [
      {
        key: "total raw data",
        label: "Total Raw Data",
        icon: faDatabase,
        color: "#6A11CB",
        gradient: "linear-gradient(135deg, #6A11CB 0%, #2575FC 100%)",
      },
      {
        key: "scrappedPosts",
        label: "On Demand Crawler",
        icon: faCloudArrowDown,
        color: "#EC4899",
        gradient: "linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)",
      },
      {
        key: "topicPosts",
        label: "Topic Posts",
        icon: faFileLines,
        color: "#14B8A6",
        gradient: "linear-gradient(135deg, #14B8A6 0%, #0EA5E9 100%)",
      },
      {
        key: "scrappedPostsTest",
        label: "Testing",
        icon: faFlask,
        color: "#F97316",
        gradient: "linear-gradient(135deg, #F97316 0%, #FACC15 100%)",
      },
    ],
  },
  {
    title: "Processed Data",
    group: "processedData",
    accent: "#8B5CF6",
    headerGradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    cards: [
      {
        key: "totalProcessed",
        label: "Total Processed",
        icon: faCircleCheck,
        color: "#6366F1",
        gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
      },
      {
        key: "topicPostsProcessed",
        label: "Topic Posts Processed",
        icon: faLayerGroup,
        color: "#0EA5E9",
        gradient: "linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)",
      },
      {
        key: "scrappedPostsStatus4",
        label: "On Demand Crawler: Section",
        icon: faListCheck,
        color: "#10B981",
        gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
      },
      {
        key: "socialMediaSentiment",
        label: "On Demand Crawler: Sentiment",
        icon: faFaceSmile,
        color: "#F59E0B",
        gradient: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
      },
    ],
  },
];

const StatCard = ({ card, value, loading, delay }) => {
  const animated = useCountUp(value, !loading);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        p: 2.25,
        borderRadius: "16px",
        backgroundColor: "rgba(255,255,255,0.6)",
        border: "1px solid rgba(226,232,240,0.8)",
        backdropFilter: "blur(6px)",
        opacity: 0,
        transform: "translateY(8px)",
        animation: `cardIn 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms forwards`,
        transition:
          "transform 0.25s cubic-bezier(0.16,1,0.3,1), box-shadow 0.25s ease, border-color 0.25s ease",
        cursor: "default",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "16px",
          padding: "1px",
          background: card.gradient,
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          opacity: 0,
          transition: "opacity 0.25s ease",
        },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 12px 28px -8px ${card.color}40`,
          "&::before": { opacity: 1 },
        },
        "@keyframes cardIn": {
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 46,
          height: 46,
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: card.gradient,
          boxShadow: `0 6px 16px -4px ${card.color}66`,
        }}
      >
        <FontAwesomeIcon
          icon={card.icon}
          style={{ color: "#ffffff", fontSize: 17 }}
        />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {loading ? (
          <Skeleton width={64} height={30} sx={{ borderRadius: 1 }} />
        ) : (
          <Typography
            sx={{
              fontFamily: "'Host Grotesk', sans-serif",
              fontWeight: 800,
              fontSize: "1.55rem",
              color: "#0f172a",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatNumber(animated)}
          </Typography>
        )}
        <Typography
          sx={{
            fontFamily: "'Host Grotesk', sans-serif",
            fontSize: "0.74rem",
            color: "#64748b",
            fontWeight: 600,
            lineHeight: 1.3,
            mt: 0.35,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          {card.label}
        </Typography>
      </Box>
    </Box>
  );
};

const DataCountsCards = () => {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem("Token");

    getDataCounts(token)
      .then((res) => {
        if (mounted) setCounts(res?.data || null);
      })
      .catch((err) => {
        console.error("Error fetching data counts:", err);
        if (mounted) setCounts(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: 3,
        mb: 3,
      }}
    >
      {SECTIONS.map((section, sIdx) => (
        <Box
          key={section.title}
          sx={{
            position: "relative",
            borderRadius: "24px",
            background:
              "linear-gradient(180deg, #ffffff 0%, #fafbff 100%)",
            border: "1px solid #eef2f7",
            boxShadow:
              "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -12px rgba(15,23,42,0.12)",
            overflow: "hidden",
            p: 2.75,
          }}
        >
          {/* ambient glow */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: section.headerGradient,
              filter: "blur(60px)",
              opacity: 0.12,
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              mb: 2.5,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 22,
                borderRadius: "4px",
                background: section.headerGradient,
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Host Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "#0f172a",
                letterSpacing: "-0.01em",
              }}
            >
              {section.title}
            </Typography>
          </Box>

          <Box
            sx={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.75,
            }}
          >
            {section.cards.map((card, idx) => (
              <StatCard
                key={card.key}
                card={card}
                value={counts?.[section.group]?.[card.key]}
                loading={loading}
                delay={sIdx * 120 + idx * 70}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DataCountsCards;